<template>
  <BaseModal
    id="preview-evidence-submission-modal"
    title="Preview Submission"
    :show.sync="$show"
    :no-close-on-backdrop="false"
    size="lg"
    hide-footer
    class="modal__preview-evidence"
    @closeModal="closeModal()"
  >
    <div class="px-1">
      <BaseLoadingState v-if="isLoading" />
      <div v-else>
        <div v-if="evidenceSubmission && learningUnit && learner" class="w-100">
          <EvidenceSubmissionUserStatus
            v-model="localEvidenceSubmissionId"
            :user="learner"
            :learning-unit="learningUnit"
            class="mb-4"
          />

          <learning-unit-score-description-card
            v-if="learningUnit.score_description"
            :learning-unit="learningUnit"
            class="mb-4"
          />

          <post-evaluation-assessment-part
            v-for="(partMeta, index) in partMetaData"
            :id="`card__part-${index + 1}-task-title`"
            :key="`card__part-${index + 1}-task-list`"
            class="mb-3 w-100 observable"
            :part="partMeta"
            :learning-unit="learningUnit"
            :post-ids="partPostIds[partMeta] ? partPostIds[partMeta] : []"
            :editable="false"
            :submittable="true"
            :readonly="true"
            mode="review"
            :assessable="true"
            :disabled="true"
            :evidence-submission="evidenceSubmission"
          />

          <post-evaluation-assessment-submit
            id="post-evaluation-assessment-submit"
            class="mb-4 w-100 observable"
            :learning-unit="learningUnit"
            :evidence-submission="evidenceSubmission"
            :post-evaluation-assessments="postEvaluationAssessments"
            :readonly="true"
            mode="review"
          >
          </post-evaluation-assessment-submit>

          <post-evaluation-assessment-history
            v-if="eventLogs"
            class="w-100"
            :items="eventLogs"
            :evidence-submission-id="localEvidenceSubmissionId"
            :part-post-ids="partPostIds"
          >
          </post-evaluation-assessment-history>
        </div>
        <div v-else class="text-center">
          <p class="text-muted">No evidence submission found.</p>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import Post from '@/models/Post';
import EventLog from '@/models/EventLog';
import LearningUnit from '@/models/LearningUnit';
import EvidenceSubmission from '@/models/EvidenceSubmission';
import Learner from '@/models/Learner';

import { usePostExtractor } from '@/composables/usePostExtractor';
import BaseModal from '@/components/base-components/BaseModal';
import EvidenceSubmissionUserStatus from '@/components/EvidenceSubmission/EvidenceSubmissionUserStatus';
import LearningUnitScoreDescriptionCard from '@/components/LearningUnit/LearningUnitScoreDescriptionCard';
import PostEvaluationAssessmentPart from '@/components/Post/PostEvaluationAssessmentPart';
import PostEvaluationAssessmentSubmit from '@/components/Post/PostEvaluationAssessmentSubmit';
import PostEvaluationAssessmentHistory from '@/components/Post/PostEvaluationAssessmentHistory';
import BaseLoadingState from '@/components/base-components/BaseLoadingState';

import { orderBy } from 'lodash';

export default {
  components: {
    BaseModal,
    EvidenceSubmissionUserStatus,
    LearningUnitScoreDescriptionCard,
    PostEvaluationAssessmentPart,
    PostEvaluationAssessmentSubmit,
    PostEvaluationAssessmentHistory,
    BaseLoadingState
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },

    evidenceSubmissionId: {
      type: Number,
      default: null
    },

    learnerId: {
      type: Number,
      default: null
    },

    learningUnitId: {
      type: Number,
      default: null
    }
  },

  data() {
    return {
      isLoading: false,
      localEvidenceSubmissionId: this.evidenceSubmissionId,
      eventLogs: []
    };
  },

  computed: {
    $show: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      }
    },

    partMetaData() {
      return Object.keys(Post.PART);
    },

    postEvaluationAssessments() {
      const posts = Post.query()
        .where('post_type', 'EVALUATION_ASSESSMENT')
        .where('learning_unit_id', this.learningUnitId)
        .get();

      return posts;
    },

    partPostIds() {
      const { extractPostIdGroupByPart } = usePostExtractor();

      const { postEvaluationAssessments = [] } = this;

      return postEvaluationAssessments.length
        ? extractPostIdGroupByPart(postEvaluationAssessments)
        : {};
    },

    learner() {
      return Learner.find(this.learnerId);
    },

    learningUnit() {
      return LearningUnit.query()
        .with('ultimate_learning_outcome')
        .whereId(this.learningUnitId)
        .first();
    },

    evidenceSubmission() {
      return EvidenceSubmission.find(this.localEvidenceSubmissionId);
    }
  },

  watch: {
    localEvidenceSubmissionId(newVal) {
      this.fetchEventLogs(newVal);
    }
  },

  created() {
    this.fetch();
    this.fetchEventLogs(this.localEvidenceSubmissionId);
  },

  methods: {
    closeModal() {
      this.$emit('closeModal');
    },

    async fetch() {
      try {
        this.isLoading = true;

        await LearningUnit.api().fetchByLearningUnitId(this.learningUnitId);

        await EvidenceSubmission.api().fetchAssessor(this.learningUnitId, {
          user: this.learnerId
        });
      } catch (error) {
        console.error('Error fetching evidence submission:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchEventLogs(id) {
      const {
        response: { data = [] }
      } = await EventLog.api().fetch({
        learning_unit_id: this.learningUnitId,
        log_group: 'Submission',
        related_id: id
      });

      if (data.length) {
        const eventLogs = data.map((event) => {
          const msg = JSON.parse(event.message);

          return {
            ...event,
            message: msg
          };
        });

        this.eventLogs = orderBy(eventLogs, 'timestamp', 'desc');
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.modal__preview-evidence {
  ::v-deep .modal-body {
    height: 100dvh;
    overflow-y: auto;
  }
}
</style>
